import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, BrowserRouter as Router, Switch } from 'react-router-dom';

import Audit from './components/audit/Audit';
import Header from './components/header/Header';
import Offices from './components/offices/Offices';

import { InteractionType } from '@azure/msal-browser';
import { reactApp, setNotificationsConfig } from '@med-fe/util';
import { flow, get } from 'lodash';
import styles from './App.module.scss';
import SIGN_IN_LOGO from './assets/images/sign-in-logo.svg';
import HooAccommodations from './components/HooAccommodations/HooAccommodations';
import AccessDenied from './components/common/AccessDenied';
import Doctors from './components/doctors/Doctors';
import { TabsExportDialog } from './components/export/TabsExportDialog';
import { ImportPage } from './components/import/ImportPage';
import { Statistics } from './components/statistics/Statistics';
import Vendors from './components/vendors/Vendors';
import { fetchAllSpecialties, fetchEquipment } from './store/common/common-actions';
import { fetchTabVisibility, fetchUserPositions, setUserRoles } from './store/user/user-actions';
import {
  checkIsDefaultScheduleChangeRequestor,
  getIsAccessDenied,
  getReadTabsPermissions,
  getUserIsMODAdmin,
  getUserPositions,
  getUserPositionsLoading,
  getWriteTabsPermissions,
  getPermittedMenus,
} from './store/user/user-reducer';
import UserRoleConfiguration from './components/UserRoleConfiguration/UserRoleConfiguration';
import HolidaySchedule from './components/HolidaySchedule/HolidaySchedule';
import BrandedFixtures from './components/branded-fixtures/BrandedFixtures';

const routes = [
  {
    path: '/offices',
    title: 'Offices',
    content: () => <Offices />,
  },
  {
    path: '/doctors',
    title: 'Doctors',
    content: () => <Doctors />,
  },
  {
    path: '/vendor',
    title: 'Vendor',
    displayTitle: 'Office Vendor',
    content: () => <Vendors />,
  },
  {
    path: '/import',
    title: 'Import',
    content: () => <ImportPage />,
  },
  {
    path: '/audit',
    title: 'Audit',
    content: () => <Audit />,
  },
  {
    path: '/dashboard',
    title: 'Dashboard',
    content: () => <Statistics />,
  },
  {
    path: '/hours-of-operation-change-request',
    title: 'Hours of Operation Change Request',
    content: () => <HooAccommodations />,
  },
  {
    path: '/user-role-configuration',
    title: 'User Role Configuration',
    content: () => <UserRoleConfiguration />,
  },
  {
    path: '/holiday-schedule',
    title: 'Holiday Schedule',
    content: () => <HolidaySchedule />,
  },
  {
    path: '/branded-fixtures',
    title: 'Branded Fixtures',
    content: () => <BrandedFixtures />,
  },
];

export default function App() {
  setNotificationsConfig();
  const { instance, accounts } = useMsal();
  const dispatch = useDispatch();
  const isAccessDenied = useSelector(getIsAccessDenied);
  const routerBasename = reactApp().routerBasename;
  const readTabsPermissions = useSelector(getReadTabsPermissions);
  const writeTabsPermissions = useSelector(getWriteTabsPermissions);
  const userIsMODAdmin = useSelector(getUserIsMODAdmin);
  const userPositions = useSelector(getUserPositions);
  const userPositionsLoading = useSelector(getUserPositionsLoading);
  const isDefaultScheduleChangeRequestor = useSelector(checkIsDefaultScheduleChangeRequestor);
  const isHooRequestAllowed =
    userPositions?.includes('RCFD') ||
    userPositions?.includes('AV') ||
    userPositions?.includes('RM') ||
    userPositions?.includes('AM') ||
    isDefaultScheduleChangeRequestor;
  const permittedMenus = useSelector(getPermittedMenus);

  useEffect(() => {
    dispatch(setUserRoles(get(instance.getActiveAccount(), 'idTokenClaims.roles', [])));

    if (get(accounts, 'length')) {
      dispatch(fetchAllSpecialties());
      dispatch(fetchEquipment());

      dispatch(fetchTabVisibility({}));
      // Fetch user positions for Template Accommodations feature
      dispatch(fetchUserPositions(accounts[0]?.username));
    }
  }, [accounts]);

  const filteredRoutes = () => {
    const isNoTabsReadAllowed = Object.values(readTabsPermissions ?? {}).every((tab) => !tab);
    const isNoTabsWriteAllowed = Object.values(writeTabsPermissions ?? {}).every((tab) => !tab);
    const filterOfficeRoute = (routes) =>
      isNoTabsReadAllowed && isNoTabsWriteAllowed ? routes.filter(({ title }) => title !== 'Offices') : routes;
    const filterDoctorsRoute = (routes) =>
      !permittedMenus.some((m) => (m || '').toLowerCase() === 'doctors')
        ? routes.filter(({ title }) => title !== 'Doctors')
        : routes;

    const base = flow(filterOfficeRoute, filterDoctorsRoute)(routes);
    // filter by permittedMenus for non-admin users
    if (userIsMODAdmin) return base;
    return base.filter(
      (r) =>
        permittedMenus.some((m: string) => (m || '').toLowerCase() === (r.title || '').toLowerCase()) ||
        (r.title === 'Hours of Operation Change Request' &&
          (isHooRequestAllowed || permittedMenus.some((m: string) => (m || '').toLowerCase() === 'hoo accommodations')))
    );
  };

  const visibleRoutes = filteredRoutes();

  return (
    <Router basename={routerBasename}>
      <div className={styles.App}>
        <div className={styles.container}>
          <Header routes={filteredRoutes()} />
          <MsalAuthenticationTemplate
            interactionType={InteractionType.Redirect}
            errorComponent={(result) => (
              <section className='login-screen'>
                <section className='login-card'>
                  <img src={SIGN_IN_LOGO} width={120} height={120} />
                  <div>{result.error?.message}</div>
                </section>
              </section>
            )}
            loadingComponent={() => (
              <section className='login-screen'>
                <section className='login-card'>
                  <img src={SIGN_IN_LOGO} width={120} height={120} />
                  <div>Validating Login</div>
                </section>
              </section>
            )}
          >
            {!userPositionsLoading && isAccessDenied ? (
              <p className={styles['unauthenticated-text']}>
                You have no permission. Please contact your administrator.
              </p>
            ) : (
              <main className={styles.main}>
                <Switch>
                  <Route exact path='/'>
                    {visibleRoutes && visibleRoutes.length ? (
                      <Redirect to={visibleRoutes[0].path} />
                    ) : !userPositionsLoading ? (
                      <AccessDenied />
                    ) : null}
                  </Route>
                  {routes.map((route, index) => {
                    // Hours of Operation Change Request special case: only allowed when isHooRequestAllowed
                    if (route.title === 'Hours of Operation Change Request') {
                      return (
                        <Route
                          key={index}
                          path={route.path}
                          render={() =>
                            isHooRequestAllowed ||
                            userIsMODAdmin ||
                            permittedMenus.some(
                              (m: string) =>
                                (m || '').toLowerCase() === (route.title || '').toLowerCase() ||
                                (m || '').toLowerCase() === 'hoo accommodations'
                            ) ? (
                              <route.content />
                            ) : !userPositionsLoading && !isHooRequestAllowed ? (
                              <AccessDenied />
                            ) : null
                          }
                        />
                      );
                    }

                    // User Role Configuration stays admin-only
                    if (route.title === 'User Role Configuration') {
                      return (
                        <Route
                          key={index}
                          path={route.path}
                          render={() =>
                            userIsMODAdmin ? <route.content /> : !userPositionsLoading ? <AccessDenied /> : null
                          }
                        />
                      );
                    }

                    // Other routes: render content if permitted, otherwise render AccessDenied when path matches
                    return (
                      <Route
                        key={index}
                        path={route.path}
                        render={() =>
                          userIsMODAdmin ||
                          permittedMenus.some(
                            (m: string) => (m || '').toLowerCase() === (route.title || '').toLowerCase()
                          ) ? (
                            <route.content />
                          ) : !userPositionsLoading ? (
                            <AccessDenied />
                          ) : null
                        }
                      />
                    );
                  })}
                </Switch>
              </main>
            )}
          </MsalAuthenticationTemplate>

          {/* <UnauthenticatedTemplate>
            <Login instance={instance} />
          </UnauthenticatedTemplate> */}
          <TabsExportDialog />
        </div>
      </div>
    </Router>
  );
}
